import { HttpClient, HttpResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedResponse } from 'app/_models/paginated-data.model';
import { Pagination } from 'app/_models/pagination.model';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingCourseQuestionSectionResponse } from 'app/_models/training/training-course-question-section-response.model';
import { TrainingCourseSection } from 'app/_models/training/training-course-section.model';
import { TrainingCourseState } from 'app/_models/training/training-course-state';
import { TrainingCourse } from 'app/_models/training/training-course.model';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { BaseHttpClientService } from './base-http-client.service';
import { TrainingType } from 'app/_models/training/training-type';

@Injectable()
export class TrainingService extends BaseHttpClientService {
  constructor(
    private http: HttpClient,
  ) {
    super()
  }

  public getSiteInductionReport() {
    const fileName = 'SiteWorkerInductionReport-' + new Date().toLocaleDateString();
    this.http.get(environment.apiUrl + 'api/ext-site-induction/report', { responseType: 'blob' }).subscribe((data) => {
      const fileURL = URL.createObjectURL(data);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.setAttribute('style', 'display: none');
      a.href = fileURL;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(fileURL);
    });
  }

  public getTrainingCoursesBySiteOwnerId(siteOwnerId): Observable<any> {
    return this.http
      .get(environment.apiUrl + 'api/ext-course/by-site-owner/' + siteOwnerId + '?size=9999&page=0')
      .pipe(catchError(this.handleError));
  }

  public getCurrentWorkerTrainingCourseList(): Observable<TrainingCourseForUser[]> {
    return (this.http.get(environment.apiUrl + 'api/ext-worker-user-course/by-current-worker-user?page=0&size=200') as any)
      .pipe(map((response) => (response as any[]).map((course) => new TrainingCourseForUser().deserialize(course))))
      .pipe(catchError(this.handleError));
  }

  public getOutageAcceptanceListForCurrentContractorManager(): Observable<TrainingCourseForUser[]> {
    let params = new HttpParams();
    params = params.append('page', '0');
    params = params.append('size', '200' );
    params = params.append('sort', 'expiryDate' );

    return (this.http.get(environment.apiUrl + 'api/ext-user-course/by-current-contractor-manager', { params } ) as any)
      .pipe(map((response) => (response as any[]).map((course) => new TrainingCourseForUser().deserialize(course))))
      .pipe(catchError(this.handleError));
  }

  public getOutageAcceptanceListByMaintenance(maintenanceId: number): Observable<TrainingCourseForUser[]> {
    let params = new HttpParams();
    params = params.append('page', '0');
    params = params.append('size', '200' );
    params = params.append('sort', 'expiryDate' );

    return (this.http.get(environment.apiUrl + 'api/ext-user-course/by-maintenance/' + maintenanceId, { params } ) as any)
      .pipe(map((response) => (response as any[]).map((course) => new TrainingCourseForUser().deserialize(course))))
      .pipe(catchError(this.handleError));
  }

  public getMaintenanceListByOutage(outageId: number, pagination: Pagination): Observable<PaginatedResponse<TrainingCourseForUser>> {
    const params = this.createQueryStrings(pagination, ['expiryDate']);

    return (this.http.get(environment.apiUrl + 'api/ext-user-course/by-course/' + outageId, { params, observe: 'response' }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, TrainingCourseForUser)))
      .pipe(catchError(this.handleError));
  }

  public getWorkerTrainingCourseListById(workerUserId: number): Observable<TrainingCourseForUser[]> {
    return (this.http.get(environment.apiUrl + 'api/ext-worker-user-course/by-worker-user/' + workerUserId + '?page=0&size=200') as any)
      .pipe(map((response) => (response as any[]).map((course) => new TrainingCourseForUser().deserialize(course))))
      .pipe(catchError(this.handleError));
  }

  public getTrainingCourseForWorkerById(workerUserCourseId: number): Observable<TrainingCourseForUser> {
    return (this.http.get(environment.apiUrl + 'api/ext-worker-user-course/' + workerUserCourseId) as any)
    .pipe(map((response) => new TrainingCourseForUser().deserialize(response)))
    .pipe(catchError(this.handleError));
  }

  public getTrainingCourseSectionForWorkerByIdAndSection(workerUserCourseId: number, sectionId: number): Observable<TrainingCourseSection> {
    return (this.http.get(environment.apiUrl + 'api/ext-worker-user-course/' + workerUserCourseId) as any)
    .pipe(
      map((response) => new TrainingCourseForUser().deserialize(response)),
      map(course => (course as TrainingCourseForUser).getSectionById(sectionId))
    )
    .pipe(catchError(this.handleError));
  }

  public addInductionToSite(siteId: number, courseId: number): Observable<any> {
    const body = {
      siteId: siteId,
      courseId: courseId,
    };
    return this.http
      .post(environment.apiUrl + 'api/ext-site/add-site-induction', body)
      .pipe(catchError(this.handleError));
  }

  public removeInductionFromSite(siteId: number, courseId: number): Observable<any> {
    const body = {
      siteId: siteId,
      courseId: courseId,
    };

    return this.http
      .post(environment.apiUrl + 'api/ext-site/remove-site-induction', body)
      .pipe(catchError(this.handleError));
  }

  public createNewTrainingCourseForWorker(body): Observable<any> {
    return (this.http.post(environment.apiUrl + 'api/ext-worker-user-course', body) as any)
    .pipe(catchError(this.handleError));
  }

  public createNewAcceptanceCourseForContractor(body): Observable<any> {
    return (this.http.post(environment.apiUrl + 'api/ext-user-course', body) as any)
    .pipe(catchError(this.handleError));
  }

  public submitSectionComplete(body): Observable<any> {
    return (this.http.post(environment.apiUrl + 'api/ext-worker-user-course/course-section', body) as any)
    .pipe(catchError(this.handleError));
  }

  public submitQuizAnswers(body): Observable<TrainingCourseQuestionSectionResponse> {
    return (this.http.post(environment.apiUrl + 'api/ext-worker-user-course/course-section/submit-quiz', body) as any)
    .pipe(map((response) => this.handleData(response as HttpResponse<any>, TrainingCourseQuestionSectionResponse)))
    .pipe(catchError(this.handleError));
  }

  public resendInductionReminder(body): Observable<any> {
    return (this.http.post(environment.apiUrl + 'api/ext-worker-user-course/induction-reminder', body) as any)
    .pipe(catchError(this.handleError));
  }

  public getFileUrl(fileName: String): Observable<string> {
    const uri = environment.apiUrl + 'api/ext-file/courseFiles/' + fileName;
    return this.http.get(uri, { responseType: 'blob' }).map((response) => {
      return URL.createObjectURL(response);
    });
  }

  public openFile(fileName: String): void {
    const uri = environment.apiUrl + 'api/ext-file/courseFiles/' + fileName;
    this.http.get(uri, { responseType: 'blob' }).subscribe((response) => {
      const fileURL = URL.createObjectURL(response);
      window.open(fileURL, '_blank');
    });
  }

  public getAllTrainingCourses(pagination: Pagination, sort: string[], nameFilter: string, typeFilter?: TrainingType): Observable<PaginatedResponse<TrainingCourse>> {
    const search = [{ key: 'name', value: nameFilter }];
    let params = this.createQueryStrings(pagination, sort, search);
    if(typeFilter){
      params = params.append('type', typeFilter);
    }
    const headers = this.applyLatestOnlyHeader();

    return (this.http.get(environment.apiUrl + `api/ext-course`, { params, observe: 'response', headers }) as any)
      .pipe(map((response) => this.handlePaginatedData(response as HttpResponse<any>, TrainingCourse)))
      .pipe(catchError(this.handleError));
  }

  public createNewTrainingCourse(body): Observable<any> {
    return (this.http.post(`${environment.apiUrl}api/ext-course`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public updateTrainingCourse(trainingCourseId, body): Observable<any> {
    return (this.http.put(`${environment.apiUrl}api/ext-course/${trainingCourseId}`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public createNewCourseQuestionSection(body): Observable<any> {
    return (this.http.post(`${environment.apiUrl}api/ext-course/course-section`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public createNewCourseVideoSection(body): Observable<any> {
    return (this.http.post(`${environment.apiUrl}api/ext-course/course-section`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public createNewCourseDocumentSection(body, document): Observable<any> {
    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append('courseSection', new Blob([JSON.stringify(body)], { type: 'application/json' }));
    formData.append('file', document, document.name);

    return (this.http.post(`${environment.apiUrl}api/ext-course/course-file-section`, formData) as any)
    .pipe(catchError((error) => {
      console.log('Error', error.error)
      if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
        throw new Error('Infected');
      } else if (error.error && error.error.status === 400 && error.error.errorKey === 'EmbeddedScript') {
        throw new Error('EmbeddedScript');
      }
      return this.handleError(error);
    }));
  }

  public reorderSections(trainingCourseId, body): Observable<any> {
    return (this.http.put(`${environment.apiUrl}api/ext-course/${trainingCourseId}/reorder-sections`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public createQuestion(body): Observable<any> {
    return (this.http.post(`${environment.apiUrl}api/ext-question`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public replaceQuestions(courseSectionId, body): Observable<any> {
    return (this.http.put(`${environment.apiUrl}api/ext-question/course-section/${courseSectionId}/replace-questions`, body) as any)
    .pipe(catchError(this.handleError));
  }

  public deleteTrainingSection(trainingSectionId: number): Observable<any> {
    return (this.http.delete(environment.apiUrl + `api/ext-course/course-section/${trainingSectionId}`) as any)
      .pipe(catchError(this.handleError));
  }

  public getTrainingCourseById(trainingCourseId): Observable<TrainingCourse> {
    return this.http.get(environment.apiUrl + 'api/ext-course/' + trainingCourseId)
      .pipe(map((response) => this.handleData(response as HttpResponse<any>, TrainingCourse)))
      .pipe(catchError(this.handleError));
  }

  public deleteInductionTraining(trainingSectionId: number): Observable<any> {
    return (this.http.delete(`${environment.apiUrl}api/ext-course/${trainingSectionId}`) as any)
      .pipe(catchError(this.handleError));
  }

  public deleteQuestionById(questionId: number): Observable<any> {
    return (this.http.delete(`${environment.apiUrl}api/ext-question/${questionId}`) as any)
      .pipe(catchError(this.handleError));
  }

  public deleteUserCourseAssigment(userCourseId: number) {
    return (this.http.delete(environment.apiUrl + 'api/ext-user-course/' + userCourseId) as any)
      .pipe(catchError(this.handleError));
  }

  public getWorkersIncompleteTrainingCourseInfo(): Observable<number> {
    return this.getCurrentWorkerTrainingCourseList().pipe(
      map(data => this.getIncompleteByType(data, 'TRAINING').length)
    );
  }

  private getIncompleteByType(data: TrainingCourseForUser[], type: 'TRAINING' | 'INDUCTION'): TrainingCourseForUser[] {
    const grouped = data
      .filter(c => c.course.type === type)
      .reduce((acc, course) => {
        const id = course.course.trainingId;
        acc[id] = acc[id] || [];
        acc[id].push(course);
        return acc;
      }, {} as Record<string, TrainingCourseForUser[]>);

    return Object.keys(grouped)
      .map(k => {
        const attempts = grouped[k];
        const hasValidCompletion = attempts.some(c =>
          c.courseProgress === TrainingCourseState.Completed &&
          !c.expired &&
          !this.mapNearExpiry(c).nearExpiry
        );
        if (hasValidCompletion) {
          return null;
        }
        const sorted = this.sortCourses(attempts);
        return sorted[0];
      })
      .filter((c): c is TrainingCourseForUser => Boolean(c));
  }

  private mapNearExpiry(cert: any): any {

    if(cert.expiryDate){
      let weekLater = new Date();
      weekLater.setDate(weekLater.getDate() + 7);
      if(new Date(cert.expiryDate) < weekLater && cert.status === "APPROVED"){
        cert.nearExpiry = true;
      }
    }
    return cert;
  }


  private sortCourses(courses: TrainingCourseForUser[]): TrainingCourseForUser[] {
    return courses.sort((a, b) => {
      // Sort by course progress
      const progressOrder = {
        [TrainingCourseState.NotStarted]: 0,
        [TrainingCourseState.InProgress]: 1,
        [TrainingCourseState.Completed]: 2,
        [TrainingCourseState.Expired]: 3,
      };
      const progressDiff = progressOrder[a.courseProgress] - progressOrder[b.courseProgress];
      if (progressDiff !== 0) {
        return progressDiff;
      }

      // Sort by expiry date within each progress group
      if (!a.expiryDate || !b.expiryDate) {return 0};
      return b.expiryDate.getTime() - a.expiryDate.getTime();
    });
  }
}
